import { apiClient } from "../utils/API/apiClient";

export interface MetricItem {
  label: string;
  value: string | number;
  description?: string;
}

export interface YouTubeAnalyticsPayload {
  title?: string;
  summary?: string;
  metrics?: MetricItem[];
  raw?: Record<string, unknown>;
}

const toTitle = (key: string) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^./, (first) => first.toUpperCase())
    .trim();

const asMetrics = (obj: Record<string, unknown>): MetricItem[] =>
  Object.entries(obj)
    .filter(([, value]) => ["string", "number"].includes(typeof value))
    .slice(0, 8)
    .map(([key, value]) => ({
      label: toTitle(key),
      value: value as string | number,
    }));

// ✅ FIXED PARSER (no duplicates, handles YouTube response)
const parseAnalytics = (data: any): YouTubeAnalyticsPayload => {
  if (!data) return { summary: "No analytics data returned yet." };

  // ✅ YouTube search API response
  if (Array.isArray(data.items)) {
    return {
      summary: "Latest videos from your channel",
      metrics: data.items.slice(0, 8).map((item: any, index: number) => ({
        label: item.snippet?.title ?? `Video ${index + 1}`,
        value: item.snippet?.channelTitle ?? "Unknown Channel",
        description: item.snippet?.publishedAt
          ? new Date(item.snippet.publishedAt).toLocaleDateString()
          : undefined,
      })),
      raw: data,
    };
  }

  // existing structured metrics
  if (Array.isArray(data.metrics)) {
    return {
      title: data.title,
      summary: data.summary,
      metrics: data.metrics,
      raw: data,
    };
  }

  // generic array
  if (Array.isArray(data)) {
    return {
      metrics: data.slice(0, 8).map((entry: any, index: number) => ({
        label: entry.label ?? entry.name ?? `Metric ${index + 1}`,
        value: entry.value ?? entry.count ?? "-",
        description: entry.description,
      })),
      raw: { list: data },
    };
  }

  // generic object fallback
  if (typeof data === "object") {
    return {
      title: data.title as string | undefined,
      summary:
        (data.summary as string | undefined) ??
        "Live values from your connected YouTube account.",
      metrics: asMetrics(data as Record<string, unknown>),
      raw: data,
    };
  }

  return { summary: String(data) };
};

const fetchFromCandidates = async (
  candidates: string[],
  userId?: string
): Promise<YouTubeAnalyticsPayload> => {
  const errors: string[] = [];

  for (const path of candidates) {
    try {
      const endpoint = userId
        ? `${path}?userId=${encodeURIComponent(userId)}`
        : path;

      const response = await apiClient<any>(endpoint, { apiType: 2 });
      return parseAnalytics(response.data);
    } catch (error: any) {
      errors.push(`${path} (${error?.status ?? "error"})`);
    }
  }

  throw new Error(
    `Unable to load YouTube analytics from configured endpoints: ${errors.join(
      ", "
    )}`
  );
};

const YT_BASE = "/api/youtube/data";

// ✅ CORRECT ENDPOINTS
export const fetchViewsWatchTime = (userId?: string) =>
  fetchFromCandidates([`${YT_BASE}/videos`, `${YT_BASE}/channel`], userId);

export const fetchEngagementInsights = (userId?: string) =>
  fetchFromCandidates([`${YT_BASE}/comments`, `${YT_BASE}/videos`], userId);
